import { CountryItem, Locale } from '@/root/shared-types'

/** Listed first when the app config has no `preferredCitizenships` (absent or empty). */
const NEIGHBOUR_COUNTRIES = ['EE', 'LV', 'LT', 'UA', 'FI', 'SE', 'RU'] as const

export type CountryOption = { type: 'country'; value: string; label: string } | { type: 'separator' }

type CountryChoice = Extract<CountryOption, { type: 'country' }>

type NeighbourCountry = (typeof NEIGHBOUR_COUNTRIES)[number]

const isNeighbourCountry = (value: string): value is NeighbourCountry =>
  NEIGHBOUR_COUNTRIES.includes(value as NeighbourCountry)

const byLabel = (a: CountryChoice, b: CountryChoice) => a.label.localeCompare(b.label)

/**
 * The tenant's preferred citizenships (`preferredCitizenships` of the app config) come first, sorted by
 * name; without any, the neighbour countries do, in catalog order. The rest follow, sorted by name.
 */
export const getCountryOptions = (
  locale: Locale,
  countryItems: CountryItem[],
  preferredCodes?: string[]
): CountryOption[] => {
  const hasPreferred = Boolean(preferredCodes?.length)
  const isFirst = (code: string) => (hasPreferred ? Boolean(preferredCodes?.includes(code)) : isNeighbourCountry(code))
  const all: CountryChoice[] = countryItems.map((c) => ({
    type: 'country',
    value: c.code,
    label: c.titles?.[locale] ?? c.title,
  }))

  const first = all.filter((c) => isFirst(c.value))
  const rest = all.filter((c) => !isFirst(c.value)).toSorted(byLabel)

  return first.length ? [...(hasPreferred ? first.toSorted(byLabel) : first), { type: 'separator' }, ...rest] : rest
}
